"use client";

import { getWeeksInMonth, type CalendarDate } from "@internationalized/date";
import { useRef } from "react";
import { useCalendarCell, useCalendarGrid } from "react-aria";
import { type CalendarState } from "react-stately";

import { cn } from "@trip/utils";

import { LoadingSpinner } from "~/components/loading-spinner";

function CalendarCell({
  isAvailable,
  state,
  date,
}: {
  date: CalendarDate;
  isAvailable: boolean;
  state: CalendarState;
}) {
  const ref = useRef(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);
  return (
    <td {...cellProps} className="text-neutral-500">
      <div
        {...buttonProps}
        className={cn(
          "relative flex aspect-square size-full flex-col items-center justify-center rounded-full",
          isDisabled
            ? "opacity-20"
            : isAvailable
              ? "font-medium"
              : "opacity-20",
          !isSelected && !isDisabled ? "text-prune-500 hover:bg-prune-200" : "",
          isAvailable && isSelected ? "bg-prune-500 text-white" : "",
        )}
        hidden={isOutsideVisibleRange}
        ref={ref}
      >
        {formattedDate}
      </div>
    </td>
  );
}

export function TourBookingCalendarGrid({
  dates,
  state,
  locale,
  isLoading,
}: {
  dates: CalendarDate[];
  isLoading: boolean;
  locale: string;
  state: CalendarState;
}) {
  const {
    gridProps: { className, ...gridProps },
    headerProps,
    weekDays,
  } = useCalendarGrid({}, state);

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <div className="relative w-full">
      {isLoading ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : null}
      <table
        {...gridProps}
        className={cn(
          isLoading ? "relative z-10 opacity-50" : null,
          "w-full",
          className,
        )}
      >
        <thead {...headerProps}>
          <tr>
            {weekDays.map((day) => (
              <th key={day}>{day}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
            <tr key={weekIndex}>
              {state.getDatesInWeek(weekIndex).map((date) => {
                return date ? (
                  <CalendarCell
                    isAvailable={dates.some(
                      (_date) => _date.compare(date) === 0,
                    )}
                    date={date}
                    key={date.toString()}
                    state={state}
                  />
                ) : (
                  <td key={null} />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
